import { useCallback, useEffect, useMemo, useState } from 'react';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Prompt from './Prompt';
import SurveyInput from './inputs/SurveyInput';
import StepActions from './StepActions';
import { OPTION_BASED_QUESTIONS } from './constants/question.types';
import { Box, SurveyTemplateParser } from '@act/storybook';
import { generateQuestionResponseSchema } from '@act/shared/validators';

export interface QuestionProps {
  question: SurveyV2.QuestionDto;
  assignedSurvey: SurveyV2.AssignedSurveyDto;
  member: UserDto;
  numberOfSteps: number;
  activeStep: number;
  questionResponse: SurveyV2.QuestionResponseDto;
  handleChange: (answer: SurveyV2.AnswerQuestionDto) => Promise<boolean>;
  handleStep: (step: number | null, checkpointId?: number) => void;
  onFormStateChange?: (isDirty: boolean) => void;
}

const Question = ({
  question,
  assignedSurvey,
  member,
  questionResponse,
  activeStep,
  numberOfSteps,
  handleChange,
  handleStep,
  onFormStateChange,
}: QuestionProps) => {
  const [prompt, setPrompt] = useState<SurveyV2.PromptDto | null>(null);

  const initialValue = useMemo(() => {
    return getValueFromResponse(questionResponse);
  }, [questionResponse]);

  const validationSchema = useMemo(() => {
    return generateQuestionResponseSchema(
      question.type,
      question.validationConfig || {}
    );
  }, [question.type, question.validationConfig]);

  const formValues = useMemo(
    () => ({
      value: initialValue,
    }),
    [initialValue]
  );

  type FormData = z.infer<typeof validationSchema>;

  const { control, getValues, reset, formState } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: formValues,
  });

  const { isDirty } = formState;

  const { isValid } = formState;

  useEffect(() => {
    onFormStateChange?.(isDirty);
  }, [isDirty, onFormStateChange]);

  useEffect(() => {
    reset(formValues);
  }, [reset, formValues]);

  const togglePrompt = useCallback(
    (prompt: SurveyV2.PromptDto) => {
      if (prompt) {
        setPrompt(prompt);
      } else {
        setPrompt(question.prompt);
      }
    },
    [question.prompt]
  );

  useEffect(() => {
    togglePrompt(question.prompt);
  }, [question.prompt, togglePrompt]);

  const handleStepChange = useCallback(
    async (step: number): Promise<void> => {
      const value = getValues().value;
      if (step > activeStep) {
        const success = await handleChange(
          getAnswerFromValue(question, assignedSurvey.id, value)
        );
        if (success) {
          // Snap to latest step after responses update (null => steps.length - 1).
          // Advancing by a fixed index races Redux and can leave the UI on the
          // answered question until the dialog is reopened.
          handleStep(null);
        }
      } else {
        handleStep(step);
      }
    },
    [
      getValues,
      activeStep,
      handleChange,
      question,
      assignedSurvey.id,
      handleStep,
    ]
  );

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Box>
        <SurveyTemplateParser
          template={question.text}
          member={member}
          assignedSurvey={assignedSurvey}
        />
      </Box>
      <Box
        className="survey-question-input"
        margin="8px 4px 0 0"
        padding="8px 12px 8px 0"
      >
        <SurveyInput
          control={control}
          name="value"
          type={question.type}
          options={question.options}
          validationConfig={question.validationConfig || {}}
          togglePrompt={togglePrompt}
        />
      </Box>
      <Box>
        {prompt && (
          <Prompt
            prompt={prompt}
            assignedSurvey={assignedSurvey}
            member={member}
          />
        )}
      </Box>
      <StepActions
        activeStep={activeStep}
        numberOfSteps={numberOfSteps}
        disableNext={!isValid}
        handleStep={handleStepChange}
      />
    </Box>
  );
};

export default Question;

const isEmpty = value => !value || (Array.isArray(value) && value.length === 0);

const getAnswerFromValue = (
  question: SurveyV2.QuestionDto,
  assignedSurveyId: number,
  value: string | number[]
): SurveyV2.AnswerQuestionDto => {
  if (
    value &&
    OPTION_BASED_QUESTIONS.includes(question.type) &&
    !Array.isArray(value)
  ) {
    value = [parseInt(value)];
  }

  return {
    questionId: question.id,
    assignedSurveyId: assignedSurveyId,
    ...(isEmpty(value)
      ? { skipped: true }
      : Array.isArray(value)
      ? { selectedOptionIds: value }
      : { textResponse: value }),
  };
};

const getValueFromResponse = (
  questionResponse: SurveyV2.QuestionResponseDto
): string | number[] => {
  if (!questionResponse || questionResponse.skipped) {
    return null;
  } else if (questionResponse.selectedOptionIds?.length > 0) {
    return questionResponse.selectedOptionIds;
  } else {
    return questionResponse.textResponse;
  }
};
